import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Button,
  IconButton,
  Link,
  Card,
  CardContent,
  Box,
  Chip,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  TextField,
} from '@mui/material';
import {
  Circle,
  Edit,
  Launch,
  Business,
  Group,
  Schedule,
  CalendarToday,
  Add,
  Delete,
} from '@mui/icons-material';
import {
  useApiGet,
  useApiSend,
  getApiErrorMessage,
} from '../../../utils/httpClientTs';
import {
  getLocalChapterInfo,
  getLocalChapterTypes,
  updateLocalChapterInfo,
  disbandLocalChapter,
  postLocalChapterMeetingTimes,
  patchLocalChapterMeetingTimes,
  deleteLocalChapterMeetingTimes,
} from '../../../endpoints/chaptersTs';
import { getCountries } from '../../../endpoints/lookupsTs';
import AlertItem from '../../../Components/AlertItem';
import {
  days_of_week,
  times_of_day,
  months,
  test_frequecy,
  monthlyTimes,
} from '../../../utils/variables';
import {
  LocalChapterInfo,
  LocalChapterInfoUpdateRequest,
  MeetingTimesRequest,
} from '../../../types/chapters';
import { canViewTransferRequests } from '../../../utils/permissions';

interface LCInfoProps {
  id: string;
  lcAccess: boolean;
  lcAction?: boolean;
  isAdmin?: boolean;
  isAdminOrSupport?: boolean;
  canManage?: boolean;
}

interface MeetingFormData {
  id?: string;
  midday: string;
  time_of_day: string;
  day_of_week_id: string;
  frequency_id: string;
  frequency_timing_id: string;
  start_month_id: string;
  end_month_id: string;
}

const LCInfo: React.FC<LCInfoProps> = ({
  id,
  lcAccess,
  lcAction = false,
  isAdmin = false,
  isAdminOrSupport = false,
  canManage = false,
}) => {
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<MeetingFormData | null>(
    null
  );
  const [meetingFormData, setMeetingFormData] = useState<MeetingFormData>({
    midday: 'am',
    time_of_day: '12:00',
    day_of_week_id: '',
    frequency_id: '',
    frequency_timing_id: '',
    start_month_id: '',
    end_month_id: '',
  });

  // State for editing basic chapter information
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [infoFormData, setInfoFormData] = useState({
    information: '',
    app_pilot_program: false,
    postal_code: '',
    country_id: '',
    type_id: '',
  });
  const [infoFormError, setInfoFormError] = useState<string | null>(null);
  const [clearLocationConfirmOpen, setClearLocationConfirmOpen] =
    useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showMeetingSuccessMessage, setShowMeetingSuccessMessage] =
    useState(false);
  const [meetingSuccessMessage, setMeetingSuccessMessage] = useState('');
  const [disbandDialogOpen, setDisbandDialogOpen] = useState(false);
  const [disbandDate, setDisbandDate] = useState('');
  const [disbandError, setDisbandError] = useState<string | null>(null);
  const [showDisbandSuccessMessage, setShowDisbandSuccessMessage] =
    useState(false);

  const { data, isFetching } = useApiGet<
    LocalChapterInfo,
    { chapterId: string }
  >(
    ['local-chapter-info', id],
    getLocalChapterInfo,
    {
      enabled: true,
      retry: 1,
    },
    { chapterId: id }
  );

  const { data: countriesList } = useApiGet(
    ['membership-countries'],
    getCountries,
    { enabled: true, retry: 1 }
  );

  const { data: chapterTypesList } = useApiGet(
    ['local-chapter-types'],
    getLocalChapterTypes,
    { enabled: true, retry: 1 }
  );

  // API mutation hooks
  const updateInfoMutation = useApiSend(
    (payload: LocalChapterInfoUpdateRequest) =>
      updateLocalChapterInfo(id, payload),
    () => {
      setInfoDialogOpen(false);
      setClearLocationConfirmOpen(false);
      setInfoFormError(null);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    },
    undefined,
    [['local-chapter-info', id]]
  );

  const createMeetingMutation = useApiSend(
    (data: MeetingTimesRequest) => postLocalChapterMeetingTimes(id, data),
    () => {
      setMeetingDialogOpen(false);
      setMeetingFormData({
        midday: 'am',
        time_of_day: '12:00',
        day_of_week_id: '',
        frequency_id: '',
        frequency_timing_id: '',
        start_month_id: '',
        end_month_id: '',
      });
      setMeetingSuccessMessage('Meeting time created successfully!');
      setShowMeetingSuccessMessage(true);
      setTimeout(() => setShowMeetingSuccessMessage(false), 3000);
    },
    undefined,
    [['local-chapter-info', id]]
  );

  const updateMeetingMutation = useApiSend(
    (data: { meetingId: string; meetingData: MeetingTimesRequest }) =>
      patchLocalChapterMeetingTimes(id, data.meetingId, data.meetingData),
    () => {
      setMeetingDialogOpen(false);
      setEditingMeeting(null);
      setMeetingFormData({
        midday: 'am',
        time_of_day: '12:00',
        day_of_week_id: '',
        frequency_id: '',
        frequency_timing_id: '',
        start_month_id: '',
        end_month_id: '',
      });
      setMeetingSuccessMessage('Meeting time updated successfully!');
      setShowMeetingSuccessMessage(true);
      setTimeout(() => setShowMeetingSuccessMessage(false), 3000);
    },
    undefined,
    [['local-chapter-info', id]]
  );

  const deleteMeetingMutation = useApiSend(
    (meetingId: string) => deleteLocalChapterMeetingTimes(id, meetingId),
    () => {
      setMeetingSuccessMessage('Meeting time deleted successfully!');
      setShowMeetingSuccessMessage(true);
      setTimeout(() => setShowMeetingSuccessMessage(false), 3000);
    },
    undefined,
    [['local-chapter-info', id]]
  );

  const disbandMutation = useApiSend(
    (payload: { disbanded_date: string }) => disbandLocalChapter(id, payload),
    () => {
      setDisbandDialogOpen(false);
      setDisbandDate('');
      setDisbandError(null);
      setShowDisbandSuccessMessage(true);
      setTimeout(() => setShowDisbandSuccessMessage(false), 5000);
    },
    (err) => setDisbandError(getApiErrorMessage(err)),
    [
      ['local-chapter-info', id],
      ['lc-chapters-detail', id],
    ]
  );

  const parseOriginationDateISO = (displayDate: string): string | null => {
    const parts = displayDate.trim().split('-');
    if (parts.length !== 3) {
      return null;
    }
    const [month, day, year] = parts;
    if (!month || !day || !year) {
      return null;
    }
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };

  const todayISO = (): string => new Date().toISOString().slice(0, 10);

  const validateDisbandDate = (value: string): string | null => {
    if (!value.trim()) {
      return 'Disband date is required.';
    }
    if (value > todayISO()) {
      return 'Disband date cannot be in the future.';
    }
    if (data?.origination_date) {
      const originationISO = parseOriginationDateISO(data.origination_date);
      if (originationISO && value < originationISO) {
        return 'Disband date cannot be before the organization date.';
      }
    }
    return null;
  };

  const handleOpenDisbandDialog = () => {
    setDisbandDate('');
    setDisbandError(null);
    setDisbandDialogOpen(true);
  };

  const handleCloseDisbandDialog = () => {
    if (disbandMutation.isPending) {
      return;
    }
    setDisbandDialogOpen(false);
    setDisbandDate('');
    setDisbandError(null);
  };

  const handleSubmitDisband = () => {
    const validationError = validateDisbandDate(disbandDate);
    if (validationError) {
      setDisbandError(validationError);
      return;
    }
    setDisbandError(null);
    disbandMutation.mutate({ disbanded_date: disbandDate });
  };

  const showAdministrativeActions =
    canManage && !data?.is_disbanded && !data?.is_merged;

  // Helper functions
  const handleOpenInfoDialog = () => {
    setInfoFormData({
      information: data?.information || '',
      app_pilot_program: data?.app_pilot_program || false,
      postal_code: data?.postal_code ?? '',
      country_id: data?.country_id ?? '',
      type_id: data?.type_id ?? '',
    });
    setInfoFormError(null);
    setInfoDialogOpen(true);
  };

  const handleCloseInfoDialog = () => {
    setInfoDialogOpen(false);
    setInfoFormError(null);
    setClearLocationConfirmOpen(false);
  };

  const buildInfoPayload = (): LocalChapterInfoUpdateRequest | null => {
    const postal = infoFormData.postal_code.trim();
    const country = infoFormData.country_id.trim();
    if ((postal && !country) || (!postal && country)) {
      setInfoFormError(
        'Postal code and country must both be set, or both cleared.'
      );
      return null;
    }
    const payload: LocalChapterInfoUpdateRequest = {
      information: infoFormData.information || null,
      postal_code: postal || null,
      country_id: country || null,
    };
    if (canEditChapterLocation() && infoFormData.type_id.trim()) {
      payload.type_id = infoFormData.type_id.trim();
    }
    if (supportAdminEdit()) {
      payload.app_pilot_program = infoFormData.app_pilot_program;
    }
    return payload;
  };

  const isClearingLocation = (): boolean => {
    const hadLocation = Boolean(
      (data?.postal_code ?? '').trim() || (data?.country_id ?? '').trim()
    );
    const postal = infoFormData.postal_code.trim();
    const country = infoFormData.country_id.trim();
    return hadLocation && !postal && !country;
  };

  const handleSubmitInfo = () => {
    setInfoFormError(null);
    const payload = buildInfoPayload();
    if (!payload) {
      return;
    }
    if (isClearingLocation()) {
      setClearLocationConfirmOpen(true);
      return;
    }
    updateInfoMutation.mutate(payload);
  };

  const handleConfirmClearLocation = () => {
    const payload = buildInfoPayload();
    if (!payload) {
      setClearLocationConfirmOpen(false);
      return;
    }
    updateInfoMutation.mutate(payload);
  };

  const handleOpenMeetingDialog = (meeting?: MeetingFormData) => {
    if (meeting) {
      setEditingMeeting(meeting);
      setMeetingFormData(meeting);
    } else {
      setEditingMeeting(null);
      setMeetingFormData({
        midday: 'am',
        time_of_day: '12:00',
        day_of_week_id: '',
        frequency_id: '',
        frequency_timing_id: '',
        start_month_id: '',
        end_month_id: '',
      });
    }
    setMeetingDialogOpen(true);
  };

  const handleCloseMeetingDialog = () => {
    setMeetingDialogOpen(false);
    setEditingMeeting(null);
    setMeetingFormData({
      midday: 'am',
      time_of_day: '12:00',
      day_of_week_id: '',
      frequency_id: '',
      frequency_timing_id: '',
      start_month_id: '',
      end_month_id: '',
    });
  };

  const handleSubmitMeeting = () => {
    if (editingMeeting) {
      updateMeetingMutation.mutate({
        meetingId: editingMeeting.id || '',
        meetingData: meetingFormData,
      });
    } else {
      createMeetingMutation.mutate(meetingFormData);
    }
  };

  const handleDeleteMeeting = (meetingId: string) => {
    if (window.confirm('Are you sure you want to delete this meeting time?')) {
      deleteMeetingMutation.mutate(meetingId);
    }
  };

  if (isFetching) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography variant="h6" color="text.secondary">
          Loading chapter information...
        </Typography>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography variant="h6" color="text.secondary">
          No chapter data available
        </Typography>
      </Box>
    );
  }

  const canLinkTransferRequests = (): boolean => {
    if (lcAccess) {
      return canViewTransferRequests();
    }
    return false;
  };

  const canEdit = (): boolean => {
    return lcAccess && lcAction;
  };

  const adminEdit = (): boolean => {
    return lcAccess && isAdmin;
  };

  const supportAdminEdit = (): boolean => {
    return lcAccess && isAdminOrSupport;
  };

  const canEditChapterLocation = (): boolean => {
    return canEdit() || adminEdit();
  };

  const getHealthColor = (color: string | null): string => {
    switch (color) {
      case 'green':
        return '#4caf50';
      case 'yellow':
        return '#ff9800';
      case 'red':
        return '#f44336';
      default:
        return '#9e9e9e';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {showSuccessMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setShowSuccessMessage(false)}
        >
          Chapter information updated successfully!
        </Alert>
      )}
      {showMeetingSuccessMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setShowMeetingSuccessMessage(false)}
        >
          {meetingSuccessMessage}
        </Alert>
      )}
      {showDisbandSuccessMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setShowDisbandSuccessMessage(false)}
        >
          Chapter disbanded successfully.
        </Alert>
      )}
      <Grid container spacing={3}>
        {/* Header Card */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                mb={2}
              >
                <Box>
                  <Typography variant="h4" component="h1" gutterBottom>
                    {data.name}
                  </Typography>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {data.abbreviation}
                  </Typography>
                  <Chip
                    label={data.is_disbanded ? 'Disbanded' : 'Active'}
                    color={data.is_disbanded ? 'error' : 'success'}
                    variant="outlined"
                  />
                </Box>
                {adminEdit() && (
                  <IconButton
                    onClick={handleOpenInfoDialog}
                    sx={{ color: '#1976d2' }}
                  >
                    <Edit />
                  </IconButton>
                )}
              </Box>

              {data.information && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1" color="text.secondary">
                    {data.information}
                  </Typography>
                  {canEdit() && (
                    <Button
                      size="small"
                      startIcon={<Edit />}
                      onClick={handleOpenInfoDialog}
                      sx={{ mt: 1, textTransform: 'none', color: '#1976d2' }}
                    >
                      Edit Description
                    </Button>
                  )}
                </Box>
              )}
              {!data.information && canEdit() && (
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={handleOpenInfoDialog}
                  sx={{ mt: 2, textTransform: 'none', color: '#2e7d32' }}
                >
                  Add Description
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Basic Info Cards */}
        <Grid item xs={12} md={6}>
          <Card elevation={1}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Business sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Chapter Details</Typography>
              </Box>
              <Stack spacing={2}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    S/P/D:
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      {data.spd_name}
                    </Typography>
                    <Link
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`/spd-chapters/${data.spd_id}`}
                    >
                      <Launch sx={{ fontSize: 16, color: 'primary.main' }} />
                    </Link>
                  </Box>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Organization Date:
                  </Typography>
                  <Typography variant="body2">
                    {data.origination_date}
                  </Typography>
                </Box>
                {data.disbanded_date && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Disbanded Date:
                    </Typography>
                    <Typography variant="body2" color="error.main">
                      {data.disbanded_date}
                    </Typography>
                  </Box>
                )}
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Chapter Meeting Type:
                  </Typography>
                  <Typography variant="body2">
                    {data.type_name?.trim()
                      ? data.type_name
                      : data.type_id
                        ? data.type_id
                        : '—'}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Postal code:
                  </Typography>
                  <Typography variant="body2">
                    {data.postal_code?.trim() ? data.postal_code : '—'}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Country:
                  </Typography>
                  <Typography variant="body2">
                    {data.country_name?.trim()
                      ? data.country_name
                      : data.country_id
                        ? data.country_id
                        : '—'}
                  </Typography>
                </Box>
                {supportAdminEdit() && (
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2" color="text.secondary">
                      App Pilot Program:
                    </Typography>
                    <Chip
                      label={data.app_pilot_program ? 'Enabled' : 'Disabled'}
                      color={data.app_pilot_program ? 'success' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={1}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Group sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Membership</Typography>
              </Box>
              <Stack spacing={2}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Active Members:
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    {data.active_members}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Transfer Requests:
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography
                      variant="h6"
                      color="primary.main"
                      sx={{ mr: 1 }}
                    >
                      {data.transfer_requests}
                    </Typography>
                    {canLinkTransferRequests() && (
                      <Link
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`/seeking-transfer?spd_id=${data.spd_id}&status_id=60baf6c89e6f4343900e4dba8188548d`}
                      >
                        <Launch sx={{ fontSize: 16, color: 'primary.main' }} />
                      </Link>
                    )}
                  </Box>
                </Box>
                {adminEdit() && data.health_color && (
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2" color="text.secondary">
                      Health Trend:
                    </Typography>
                    <Circle
                      sx={{
                        color: getHealthColor(data.health_color),
                        fontSize: 20,
                      }}
                    />
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Meeting Times Card */}
        <Grid item xs={12}>
          <Card elevation={1}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Schedule sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Meeting Schedule</Typography>
                {canEdit() && (
                  <Button
                    startIcon={<Add />}
                    variant="contained"
                    size="small"
                    onClick={() => handleOpenMeetingDialog()}
                    sx={{ ml: 'auto' }}
                    color="success"
                  >
                    Add Meeting
                  </Button>
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />
              {data.meeting_times && data.meeting_times.length > 0 ? (
                <Stack spacing={2}>
                  {data.meeting_times.map((meeting, index) => (
                    <Box
                      key={index}
                      sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}
                    >
                      <Box display="flex" alignItems="center" mb={1}>
                        <CalendarToday
                          sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }}
                        />
                        <Typography variant="body2" fontWeight="medium">
                          {meeting.start_month === meeting.end_month
                            ? `${meeting.start_month} - ${meeting.frequency_timing} ${meeting.day_of_week}`
                            : `${meeting.start_month} thru ${meeting.end_month} - ${meeting.frequency} ${meeting.frequency_timing} ${meeting.day_of_week}`}
                        </Typography>
                        {canEdit() && (
                          <Box sx={{ ml: 'auto' }}>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleOpenMeetingDialog({
                                  id: meeting.id,
                                  midday: meeting.midday,
                                  time_of_day: meeting.time_of_day,
                                  day_of_week_id: meeting.day_of_week_id,
                                  frequency_id: meeting.frequency_id,
                                  frequency_timing_id:
                                    meeting.frequency_timing_id,
                                  start_month_id: meeting.start_month_id,
                                  end_month_id: meeting.end_month_id,
                                })
                              }
                              sx={{ color: '#1976d2' }}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteMeeting(meeting.id)}
                              sx={{ color: 'error.main' }}
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {meeting.time_of_day} {meeting.midday}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Box textAlign="center" py={3}>
                  <Typography variant="body2" color="text.secondary">
                    No meeting times scheduled
                  </Typography>
                  {canEdit() && (
                    <Button
                      startIcon={<Add />}
                      variant="outlined"
                      size="small"
                      onClick={() => handleOpenMeetingDialog()}
                      sx={{ mt: 1, color: '#2e7d32', borderColor: '#2e7d32' }}
                    >
                      Add First Meeting
                    </Button>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Admin Actions */}
        {showAdministrativeActions && (
          <Grid item xs={12}>
            <Card elevation={1}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Administrative Actions
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleOpenDisbandDialog}
                    startIcon={<Circle />}
                  >
                    Disband Chapter
                  </Button>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => console.log('Merge Chapter')}
                    startIcon={<Business />}
                  >
                    Merge Chapter
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Information Edit Dialog */}
      <Dialog
        open={infoDialogOpen}
        onClose={handleCloseInfoDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Chapter Information</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Chapter Description"
                value={infoFormData.information || ''}
                onChange={(e) =>
                  setInfoFormData({
                    ...infoFormData,
                    information: e.target.value,
                  })
                }
                placeholder="Enter a description of the chapter's activities, focus areas, or meeting style..."
                helperText="Provide information about what members can expect from this chapter"
              />
            </Grid>
            {canEditChapterLocation() && (
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="lc-info-type-label">
                      Chapter Meeting Type
                    </InputLabel>
                    <Select
                      labelId="lc-info-type-label"
                      label="Chapter Meeting Type"
                      value={infoFormData.type_id || ''}
                      onChange={(e) =>
                        setInfoFormData({
                          ...infoFormData,
                          type_id: e.target.value as string,
                        })
                      }
                    >
                      {(chapterTypesList ?? []).map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Postal code"
                    value={infoFormData.postal_code}
                    onChange={(e) => {
                      const next = e.target.value;
                      setInfoFormData((prev) => ({
                        ...prev,
                        postal_code: next,
                        country_id: next.trim() === '' ? '' : prev.country_id,
                      }));
                      setInfoFormError(null);
                    }}
                    placeholder="Chapter mailing postal / ZIP code"
                    helperText="Postal code and country must both be set or both cleared"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="lc-info-country-label">Country</InputLabel>
                    <Select
                      labelId="lc-info-country-label"
                      label="Country"
                      value={infoFormData.country_id || ''}
                      onChange={(e) => {
                        const next = e.target.value as string;
                        setInfoFormData((prev) => ({
                          ...prev,
                          country_id: next,
                          postal_code:
                            next.trim() === '' ? '' : prev.postal_code,
                        }));
                        setInfoFormError(null);
                      }}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {(countriesList ?? []).map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            {infoFormError && (
              <Grid item xs={12}>
                <Alert severity="error">{infoFormError}</Alert>
              </Grid>
            )}
            {supportAdminEdit() && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>App Pilot Program</InputLabel>
                  <Select
                    value={
                      infoFormData.app_pilot_program ? 'enabled' : 'disabled'
                    }
                    onChange={(e) =>
                      setInfoFormData({
                        ...infoFormData,
                        app_pilot_program: e.target.value === 'enabled',
                      })
                    }
                    label="App Pilot Program"
                  >
                    <MenuItem value="enabled">Enabled</MenuItem>
                    <MenuItem value="disabled">Disabled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>

          {updateInfoMutation.isError && (
            <Box sx={{ mt: 2 }}>
              <AlertItem
                severity="error"
                description={
                  getApiErrorMessage(updateInfoMutation.error) ||
                  'An error occurred while updating chapter information. Please try again.'
                }
                onClose={() => {}}
                keepOpen={true}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInfoDialog}>Cancel</Button>
          <Button
            onClick={handleSubmitInfo}
            variant="contained"
            disabled={updateInfoMutation.isPending}
          >
            {updateInfoMutation.isPending
              ? 'Updating...'
              : 'Update Information'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={clearLocationConfirmOpen}
        onClose={() => setClearLocationConfirmOpen(false)}
      >
        <DialogTitle>Clear chapter location?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Clearing postal code and country will remove this chapter&apos;s map
            location (latitude/longitude and geocode status). Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setClearLocationConfirmOpen(false)}
            disabled={updateInfoMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmClearLocation}
            color="warning"
            variant="contained"
            disabled={updateInfoMutation.isPending}
          >
            {updateInfoMutation.isPending ? 'Clearing…' : 'Clear location'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Meeting Dialog */}
      <Dialog
        open={meetingDialogOpen}
        onClose={handleCloseMeetingDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingMeeting ? 'Edit Meeting Time' : 'Add New Meeting Time'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Day of Week</InputLabel>
                <Select
                  value={meetingFormData.day_of_week_id}
                  onChange={(e) =>
                    setMeetingFormData({
                      ...meetingFormData,
                      day_of_week_id: e.target.value,
                    })
                  }
                  label="Day of Week"
                >
                  {days_of_week.map((day) => (
                    <MenuItem key={day.id} value={day.id}>
                      {day.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Time of Day</InputLabel>
                <Select
                  value={meetingFormData.time_of_day}
                  onChange={(e) =>
                    setMeetingFormData({
                      ...meetingFormData,
                      time_of_day: e.target.value,
                    })
                  }
                  label="Time of Day"
                >
                  {times_of_day.map((time) => (
                    <MenuItem key={time.id} value={time.id}>
                      {time.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>AM/PM</InputLabel>
                <Select
                  value={meetingFormData.midday}
                  onChange={(e) =>
                    setMeetingFormData({
                      ...meetingFormData,
                      midday: e.target.value,
                    })
                  }
                  label="AM/PM"
                >
                  <MenuItem value="am">AM</MenuItem>
                  <MenuItem value="pm">PM</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={meetingFormData.frequency_id}
                  onChange={(e) =>
                    setMeetingFormData({
                      ...meetingFormData,
                      frequency_id: e.target.value,
                    })
                  }
                  label="Frequency"
                >
                  {test_frequecy.map((freq) => (
                    <MenuItem key={freq.id} value={freq.id}>
                      {freq.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Frequency Timing</InputLabel>
                <Select
                  value={meetingFormData.frequency_timing_id}
                  onChange={(e) =>
                    setMeetingFormData({
                      ...meetingFormData,
                      frequency_timing_id: e.target.value,
                    })
                  }
                  label="Frequency Timing"
                >
                  {monthlyTimes.map((timing) => (
                    <MenuItem key={timing.id} value={timing.id}>
                      {timing.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Start Month</InputLabel>
                <Select
                  value={meetingFormData.start_month_id}
                  onChange={(e) =>
                    setMeetingFormData({
                      ...meetingFormData,
                      start_month_id: e.target.value,
                    })
                  }
                  label="Start Month"
                >
                  {months.map((month) => (
                    <MenuItem key={month.id} value={month.id}>
                      {month.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>End Month</InputLabel>
                <Select
                  value={meetingFormData.end_month_id}
                  onChange={(e) =>
                    setMeetingFormData({
                      ...meetingFormData,
                      end_month_id: e.target.value,
                    })
                  }
                  label="End Month"
                >
                  {months.map((month) => (
                    <MenuItem key={month.id} value={month.id}>
                      {month.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {(createMeetingMutation.isError ||
            updateMeetingMutation.isError ||
            deleteMeetingMutation.isError) && (
            <Box sx={{ mt: 2 }}>
              <AlertItem
                severity="error"
                description="An error occurred. Please try again."
                onClose={() => {}}
                keepOpen={true}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMeetingDialog}>Cancel</Button>
          <Button
            onClick={handleSubmitMeeting}
            variant="contained"
            disabled={
              !meetingFormData.day_of_week_id ||
              !meetingFormData.frequency_id ||
              !meetingFormData.frequency_timing_id ||
              !meetingFormData.start_month_id ||
              !meetingFormData.end_month_id
            }
          >
            {editingMeeting ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={disbandDialogOpen}
        onClose={handleCloseDisbandDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Disband Chapter</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Disband {data.name} on the selected date? This ends all active group
            memberships for this chapter and cannot be undone.
          </Typography>
          <TextField
            fullWidth
            required
            type="date"
            label="Disband date"
            value={disbandDate}
            onChange={(e) => {
              setDisbandDate(e.target.value);
              if (disbandError) {
                setDisbandError(null);
              }
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: todayISO(),
              min: data.origination_date
                ? (parseOriginationDateISO(data.origination_date) ?? undefined)
                : undefined,
            }}
            helperText={
              data.origination_date
                ? `Organization date: ${data.origination_date}`
                : undefined
            }
            error={Boolean(disbandError)}
          />
          {disbandError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {disbandError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDisbandDialog}
            disabled={disbandMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitDisband}
            variant="contained"
            color="error"
            disabled={disbandMutation.isPending || !disbandDate.trim()}
          >
            {disbandMutation.isPending ? 'Disbanding…' : 'Disband Chapter'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LCInfo;
